/**
 * Access Profile API Service
 *
 * Service para interagir com API de perfis de acesso (auth_item).
 * Gerencia RBAC: roles, tasks e operations.
 *
 * @since 2025-12-22
 * @epic INIT-0046
 */

import { api } from './apiClient';
import type {
  AuthItem,
  AuthItemCreate,
  AuthItemUpdate,
  AuthAssignment,
  AuthItemChild,
  AccessProfileListParams,
  // Image Types
  PersonImageType,
  PersonImageTypeCreate,
  PersonImageTypeUpdate,
  // Relationship Types
  PersonRelationshipType,
  PersonRelationshipTypeCreate,
  PersonRelationshipTypeUpdate,
  // Family Relationship Types
  FamilyRelationshipType,
  FamilyRelationshipTypeCreate,
  FamilyRelationshipTypeUpdate,
  AccessListParams,
  // Auth Assignments
  AuthAssignmentItem,
  AuthAssignmentListResponse,
  AuthAssignmentListParams,
  AuthAssignmentCreate,
} from '@/types/access';

// ==========================================
// TYPES - Respostas da API Python
// ==========================================

interface PythonAuthItemResponse {
  name: string;
  type: number;
  description: string | null;
  bizrule: string | null;
  data: string | null;
  order_hierarchical: number | null;
  title: string | null;
  require_mfa: boolean;
  status: number;
  created_at?: string;
  users_count?: number;
  children_count?: number;
}

interface PythonPaginatedResponse {
  items: PythonAuthItemResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ==========================================
// HELPERS
// ==========================================

/**
 * Converte resposta Python (snake_case) para TypeScript (camelCase)
 */
function transformAuthItem(item: PythonAuthItemResponse): AuthItem {
  return {
    name: item.name,
    type: item.type,
    description: item.description,
    bizrule: item.bizrule,
    data: item.data,
    orderHierarchical: item.order_hierarchical,
    title: item.title,
    requireMfa: item.require_mfa ?? false,
    status: item.status,
    createdAt: item.created_at,
    usersCount: item.users_count,
    childrenCount: item.children_count,
  };
}

/**
 * Converte dados TypeScript (camelCase) para Python (snake_case)
 */
function transformToApi(data: AuthItemCreate | AuthItemUpdate): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if ('name' in data && data.name !== undefined) result.name = data.name;
  if ('type' in data && data.type !== undefined) result.type = data.type;
  if (data.description !== undefined) result.description = data.description;
  if (data.bizrule !== undefined) result.bizrule = data.bizrule;
  if (data.data !== undefined) result.data = data.data;
  if (data.orderHierarchical !== undefined) result.order_hierarchical = data.orderHierarchical;
  if (data.title !== undefined) result.title = data.title;
  if (data.requireMfa !== undefined) result.require_mfa = data.requireMfa;
  if ('status' in data && data.status !== undefined) result.status = data.status;

  return result;
}

// ==========================================
// API ENDPOINTS
// ==========================================

const BASE_URL = '/person/access/profiles';

/**
 * Lista perfis de acesso (auth_item)
 */
export async function listAccessProfiles(
  params: AccessProfileListParams = {}
): Promise<{ items: AuthItem[]; total: number }> {
  const queryParams = new URLSearchParams();

  if (params.type !== undefined) queryParams.append('type', params.type.toString());
  if (params.status !== undefined) queryParams.append('status', params.status.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('page_size', params.pageSize.toString());

  const url = queryParams.toString() ? `${BASE_URL}?${queryParams}` : BASE_URL;

  try {
    const response = await api.get<PythonAuthItemResponse[] | PythonPaginatedResponse>(url);

    // API pode retornar array simples ou resposta paginada
    if (Array.isArray(response)) {
      return {
        items: response.map(transformAuthItem),
        total: response.length,
      };
    }

    return {
      items: response.items.map(transformAuthItem),
      total: response.total,
    };
  } catch (error) {
    console.error('Error listing access profiles:', error);
    throw error;
  }
}

/**
 * Obtém um perfil de acesso por nome
 */
export async function getAccessProfile(name: string): Promise<AuthItem> {
  const response = await api.get<PythonAuthItemResponse>(`${BASE_URL}/${encodeURIComponent(name)}`);
  return transformAuthItem(response);
}

/**
 * Cria um novo perfil de acesso
 */
export async function createAccessProfile(data: AuthItemCreate): Promise<AuthItem> {
  const payload = transformToApi(data);
  const response = await api.post<PythonAuthItemResponse>(BASE_URL, payload);
  return transformAuthItem(response);
}

/**
 * Atualiza um perfil de acesso existente
 */
export async function updateAccessProfile(name: string, data: AuthItemUpdate): Promise<AuthItem> {
  const payload = transformToApi(data);
  const response = await api.put<PythonAuthItemResponse>(
    `${BASE_URL}/${encodeURIComponent(name)}`,
    payload
  );
  return transformAuthItem(response);
}

/**
 * Exclui um perfil de acesso
 */
export async function deleteAccessProfile(name: string): Promise<void> {
  await api.delete(`${BASE_URL}/${encodeURIComponent(name)}`);
}

/**
 * Lista filhos de um perfil (auth_item_child)
 */
export async function listProfileChildren(name: string): Promise<AuthItemChild[]> {
  const response = await api.get<AuthItemChild[]>(
    `${BASE_URL}/${encodeURIComponent(name)}/children`
  );
  return response;
}

/**
 * Adiciona filho a um perfil
 */
export async function addProfileChild(parent: string, child: string): Promise<AuthItemChild> {
  const response = await api.post<AuthItemChild>(
    `${BASE_URL}/${encodeURIComponent(parent)}/children`,
    { child }
  );
  return response;
}

/**
 * Remove filho de um perfil
 */
export async function removeProfileChild(parent: string, child: string): Promise<void> {
  await api.delete(
    `${BASE_URL}/${encodeURIComponent(parent)}/children/${encodeURIComponent(child)}`
  );
}

/**
 * Lista atribuições de um perfil (auth_assignment)
 */
export async function listProfileAssignments(name: string): Promise<AuthAssignment[]> {
  const response = await api.get<AuthAssignment[]>(
    `${BASE_URL}/${encodeURIComponent(name)}/assignments`
  );
  return response;
}

/**
 * Conta usuários com um perfil
 */
export async function countProfileUsers(name: string): Promise<number> {
  const response = await api.get<{ count: number }>(
    `${BASE_URL}/${encodeURIComponent(name)}/users/count`
  );
  return response.count;
}

// ==========================================
// LOGIN LOGS
// ==========================================

export interface LoginLogEntry {
  id: number;
  userId: number;
  userName: string;
  action: string;
  actionCode: number;
  description: string;
  ipAddress: string | null;
  userAgent: string | null;
  deviceType: string | null;
  deviceBrowser: string | null;
  country: string | null;
  city: string | null;
  success: boolean;
  errorMessage: string | null;
  timestamp: string;
}

export interface LoginLogStats {
  totalEntries: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  loginCount: number;
  logoutCount: number;
}

export interface LoginLogResponse {
  success: boolean;
  items: LoginLogEntry[];
  stats: LoginLogStats;
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}

export interface LoginLogParams {
  page?: number;
  pageSize?: number;
  action?: number;
  success?: boolean;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface LoginLogAction {
  value: number;
  name: string;
  label: string;
}

interface PythonLoginLogResponse {
  success: boolean;
  items: Array<{
    id: number;
    user_id: number;
    user_name: string;
    action: string;
    action_code: number;
    description: string;
    ip_address: string | null;
    user_agent: string | null;
    device_type: string | null;
    device_browser: string | null;
    country: string | null;
    city: string | null;
    success: boolean;
    error_message: string | null;
    timestamp: string;
  }>;
  stats: {
    total_entries: number;
    success_count: number;
    failure_count: number;
    success_rate: number;
    login_count: number;
    logout_count: number;
  };
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

function transformLoginLogEntry(item: PythonLoginLogResponse['items'][0]): LoginLogEntry {
  return {
    id: item.id,
    userId: item.user_id,
    userName: item.user_name,
    action: item.action,
    actionCode: item.action_code,
    description: item.description,
    ipAddress: item.ip_address,
    userAgent: item.user_agent,
    deviceType: item.device_type,
    deviceBrowser: item.device_browser,
    country: item.country,
    city: item.city,
    success: item.success,
    errorMessage: item.error_message,
    timestamp: item.timestamp,
  };
}

function transformLoginLogStats(stats: PythonLoginLogResponse['stats']): LoginLogStats {
  return {
    totalEntries: stats.total_entries,
    successCount: stats.success_count,
    failureCount: stats.failure_count,
    successRate: stats.success_rate,
    loginCount: stats.login_count,
    logoutCount: stats.logout_count,
  };
}

const LOGIN_LOGS_URL = '/person/access/login-logs';

/**
 * Lista logs de login
 */
export async function listLoginLogs(params: LoginLogParams = {}): Promise<LoginLogResponse> {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.action !== undefined) queryParams.append('action', params.action.toString());
  if (params.success !== undefined) queryParams.append('success', params.success.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);

  const url = queryParams.toString() ? `${LOGIN_LOGS_URL}?${queryParams}` : LOGIN_LOGS_URL;

  const response = await api.get<PythonLoginLogResponse>(url);

  return {
    success: response.success,
    items: response.items.map(transformLoginLogEntry),
    stats: transformLoginLogStats(response.stats),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
    pages: response.pages,
  };
}

/**
 * Lista tipos de ações disponíveis
 */
export async function listLoginLogActions(): Promise<LoginLogAction[]> {
  const response = await api.get<{ success: boolean; data: LoginLogAction[] }>(
    `${LOGIN_LOGS_URL}/actions`
  );
  return response.data;
}

// ==========================================
// LOGIN LOG AGGREGATION & EXPORT
// ==========================================

export interface AggregationItem {
  label: string;
  count: number;
}

export interface LoginLogAggregationResponse {
  success: boolean;
  byProfile: AggregationItem[];
  byRegion: AggregationItem[];
  byCity: AggregationItem[];
}

interface PythonLoginLogAggregationResponse {
  success: boolean;
  by_profile: Array<{ label: string; count: number }>;
  by_region: Array<{ label: string; count: number }>;
  by_city: Array<{ label: string; count: number }>;
}

/**
 * Obtém dados agregados para gráficos (por perfil, região, cidade)
 */
export async function getLoginLogAggregation(params: LoginLogParams = {}): Promise<LoginLogAggregationResponse> {
  const queryParams = new URLSearchParams();

  if (params.action !== undefined) queryParams.append('action', params.action.toString());
  if (params.success !== undefined) queryParams.append('success', params.success.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);

  const url = queryParams.toString() ? `${LOGIN_LOGS_URL}/stats/aggregation?${queryParams}` : `${LOGIN_LOGS_URL}/stats/aggregation`;

  const response = await api.get<PythonLoginLogAggregationResponse>(url);

  return {
    success: response.success,
    byProfile: response.by_profile,
    byRegion: response.by_region,
    byCity: response.by_city,
  };
}

/**
 * Exporta logs de acesso para Excel (.xlsx)
 */
export async function exportLoginLogs(params: LoginLogParams = {}): Promise<Blob> {
  const queryParams = new URLSearchParams();

  if (params.action !== undefined) queryParams.append('action', params.action.toString());
  if (params.success !== undefined) queryParams.append('success', params.success.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);

  const url = queryParams.toString() ? `${LOGIN_LOGS_URL}/export?${queryParams}` : `${LOGIN_LOGS_URL}/export`;

  return api.get<Blob>(url, { responseType: 'blob' });
}

// ==========================================
// PERSON IMAGE TYPE API
// ==========================================

const IMAGE_TYPE_URL = '/person/access/image-types';

export const personImageTypeApi = {
  async list(params: AccessListParams = {}): Promise<PersonImageType[]> {
    const queryParams = new URLSearchParams();
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    const url = queryParams.toString() ? `${IMAGE_TYPE_URL}/?${queryParams}` : `${IMAGE_TYPE_URL}/`;
    return api.get<PersonImageType[]>(url);
  },

  async get(id: number): Promise<PersonImageType> {
    return api.get<PersonImageType>(`${IMAGE_TYPE_URL}/${id}`);
  },

  async create(data: PersonImageTypeCreate): Promise<PersonImageType> {
    return api.post<PersonImageType>(`${IMAGE_TYPE_URL}/`, data);
  },

  async update(id: number, data: PersonImageTypeUpdate): Promise<PersonImageType> {
    return api.put<PersonImageType>(`${IMAGE_TYPE_URL}/${id}`, data);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`${IMAGE_TYPE_URL}/${id}`);
  },
};

// ==========================================
// PERSON RELATIONSHIP TYPE API
// ==========================================

const RELATIONSHIP_TYPE_URL = '/person/access/relationship-types';

export const personRelationshipTypeApi = {
  async list(params: AccessListParams = {}): Promise<PersonRelationshipType[]> {
    const queryParams = new URLSearchParams();
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    const url = queryParams.toString() ? `${RELATIONSHIP_TYPE_URL}/?${queryParams}` : `${RELATIONSHIP_TYPE_URL}/`;
    return api.get<PersonRelationshipType[]>(url);
  },

  async get(id: number): Promise<PersonRelationshipType> {
    return api.get<PersonRelationshipType>(`${RELATIONSHIP_TYPE_URL}/${id}`);
  },

  async create(data: PersonRelationshipTypeCreate): Promise<PersonRelationshipType> {
    return api.post<PersonRelationshipType>(`${RELATIONSHIP_TYPE_URL}/`, data);
  },

  async update(id: number, data: PersonRelationshipTypeUpdate): Promise<PersonRelationshipType> {
    return api.put<PersonRelationshipType>(`${RELATIONSHIP_TYPE_URL}/${id}`, data);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`${RELATIONSHIP_TYPE_URL}/${id}`);
  },

  async count(id: number): Promise<number> {
    const response = await api.get<{ count: number }>(`${RELATIONSHIP_TYPE_URL}/${id}/count`);
    return response.count;
  },
};

// ==========================================
// FAMILY RELATIONSHIP TYPE API
// ==========================================

const FAMILY_RELATIONSHIP_TYPE_URL = '/person/access/family-relationship-types';

export const familyRelationshipTypeApi = {
  async list(params: AccessListParams = {}): Promise<FamilyRelationshipType[]> {
    const queryParams = new URLSearchParams();
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    const url = queryParams.toString() ? `${FAMILY_RELATIONSHIP_TYPE_URL}/?${queryParams}` : `${FAMILY_RELATIONSHIP_TYPE_URL}/`;
    return api.get<FamilyRelationshipType[]>(url);
  },

  async get(id: number): Promise<FamilyRelationshipType> {
    return api.get<FamilyRelationshipType>(`${FAMILY_RELATIONSHIP_TYPE_URL}/${id}`);
  },

  async create(data: FamilyRelationshipTypeCreate): Promise<FamilyRelationshipType> {
    return api.post<FamilyRelationshipType>(`${FAMILY_RELATIONSHIP_TYPE_URL}/`, data);
  },

  async update(id: number, data: FamilyRelationshipTypeUpdate): Promise<FamilyRelationshipType> {
    return api.put<FamilyRelationshipType>(`${FAMILY_RELATIONSHIP_TYPE_URL}/${id}`, data);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`${FAMILY_RELATIONSHIP_TYPE_URL}/${id}`);
  },

  async listByGender(gender: 'M' | 'F' | 'N'): Promise<FamilyRelationshipType[]> {
    return api.get<FamilyRelationshipType[]>(`${FAMILY_RELATIONSHIP_TYPE_URL}/gender/${gender}`);
  },
};

// ==========================================
// EXPORT
// ==========================================

// ==========================================
// PERSON LOGIN API (Gerenciamento de Logins)
// ==========================================

import type {
  PersonLoginListParams,
  PersonLoginListResponse,
  PersonLoginListItem,
  PersonLoginCreate,
  PersonLoginUpdate,
  PersonLoginResponse,
  AvailableProfile,
} from '@/types/access';

const LOGINS_URL = '/person/logins';

interface PythonLoginListResponse {
  success: boolean;
  items: Array<{
    id: number;
    login: string;
    status: number;
    status_name: string;
    person_id: number;
    person_name: string;
    person_document: string | null;
    person_email: string | null;
    profile_name: string | null;
    profile_title: string | null;
    last_login: string | null;
    mfa_enabled: boolean;
    datetime_inclusion: string | null;
  }>;
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

interface PythonLoginResponse {
  id: number;
  login: string;
  status: number;
  status_name: string;
  person_id: number;
  person_name: string;
  profile_name: string | null;
  mfa_enabled: boolean;
  must_change_password: boolean;
  last_login: string | null;
  datetime_inclusion: string | null;
}

function transformLoginListItem(item: PythonLoginListResponse['items'][0]): PersonLoginListItem {
  return {
    id: item.id,
    login: item.login,
    status: item.status,
    statusName: item.status_name,
    personId: item.person_id,
    personName: item.person_name,
    personDocument: item.person_document,
    personEmail: item.person_email,
    profileName: item.profile_name,
    profileTitle: item.profile_title,
    lastLogin: item.last_login,
    mfaEnabled: item.mfa_enabled,
    datetimeInclusion: item.datetime_inclusion,
  };
}

function transformLoginResponse(item: PythonLoginResponse): PersonLoginResponse {
  return {
    id: item.id,
    login: item.login,
    status: item.status,
    statusName: item.status_name,
    personId: item.person_id,
    personName: item.person_name,
    profileName: item.profile_name,
    mfaEnabled: item.mfa_enabled,
    mustChangePassword: item.must_change_password,
    lastLogin: item.last_login,
    datetimeInclusion: item.datetime_inclusion,
  };
}

export const personLoginApi = {
  /**
   * Lista logins de pessoas
   */
  async list(params: PersonLoginListParams = {}): Promise<PersonLoginListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    if (params.profile) queryParams.append('profile', params.profile);

    const url = queryParams.toString() ? `${LOGINS_URL}?${queryParams}` : LOGINS_URL;
    const response = await api.get<PythonLoginListResponse>(url);

    return {
      success: response.success,
      items: response.items.map(transformLoginListItem),
      total: response.total,
      page: response.page,
      pageSize: response.page_size,
      pages: response.pages,
    };
  },

  /**
   * Obtém login por ID
   */
  async get(id: number): Promise<PersonLoginResponse> {
    const response = await api.get<PythonLoginResponse>(`${LOGINS_URL}/${id}`);
    return transformLoginResponse(response);
  },

  /**
   * Cria novo login
   */
  async create(data: PersonLoginCreate): Promise<PersonLoginResponse> {
    const payload = {
      person_id: data.personId,
      login: data.login,
      password: data.password,
      profile_name: data.profileName,
      status: data.status ?? 1,
    };
    const response = await api.post<PythonLoginResponse>(LOGINS_URL, payload);
    return transformLoginResponse(response);
  },

  /**
   * Atualiza login
   */
  async update(id: number, data: PersonLoginUpdate): Promise<PersonLoginResponse> {
    const payload: Record<string, unknown> = {};
    if (data.login !== undefined) payload.login = data.login;
    if (data.password !== undefined) payload.password = data.password;
    if (data.profileName !== undefined) payload.profile_name = data.profileName;
    if (data.status !== undefined) payload.status = data.status;
    if (data.mfaEnabled !== undefined) payload.mfa_enabled = data.mfaEnabled;
    if (data.mustChangePassword !== undefined) payload.must_change_password = data.mustChangePassword;

    const response = await api.put<PythonLoginResponse>(`${LOGINS_URL}/${id}`, payload);
    return transformLoginResponse(response);
  },

  /**
   * Exclui login
   */
  async delete(id: number): Promise<void> {
    await api.delete(`${LOGINS_URL}/${id}`);
  },

  /**
   * Reset de senha
   */
  async resetPassword(id: number, newPassword: string, forceChange: boolean = true): Promise<PersonLoginResponse> {
    const response = await api.post<PythonLoginResponse>(`${LOGINS_URL}/${id}/reset-password`, {
      new_password: newPassword,
      force_change: forceChange,
    });
    return transformLoginResponse(response);
  },

  /**
   * Alterna status do login
   */
  async toggleStatus(id: number): Promise<PersonLoginResponse> {
    const response = await api.post<PythonLoginResponse>(`${LOGINS_URL}/${id}/toggle-status`);
    return transformLoginResponse(response);
  },

  /**
   * Lista perfis disponíveis
   */
  async listAvailableProfiles(): Promise<AvailableProfile[]> {
    const response = await api.get<{ success: boolean; items: AvailableProfile[] }>(
      `${LOGINS_URL}/profiles/available`
    );
    return response.items;
  },
};

// ==========================================
// AUTH ASSIGNMENT API (Gerenciar Acessos)
// ==========================================

const ASSIGNMENTS_URL = '/person/access/assignments';

/**
 * Interface de resposta do Python para Assignment
 */
interface PythonAuthAssignmentResponse {
  id: number;
  itemname: string;
  userid: number;
  created_at: string | null;
  login_username: string | null;
  person_id: number | null;
  person_name: string | null;
  person_document: string | null;
  profile_title: string | null;
  profile_type: number | null;
}

interface PythonAuthAssignmentListResponse {
  items: PythonAuthAssignmentResponse[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

/**
 * Transforma resposta Python para TypeScript (snake_case para camelCase)
 */
function transformAssignment(item: PythonAuthAssignmentResponse): AuthAssignmentItem {
  return {
    id: item.id,
    itemname: item.itemname,
    userid: item.userid,
    createdAt: item.created_at,
    loginUsername: item.login_username,
    personId: item.person_id,
    personName: item.person_name,
    personDocument: item.person_document,
    profileTitle: item.profile_title,
    profileType: item.profile_type,
  };
}

/**
 * API para gerenciar atribuições de acesso (auth_assignment)
 */
export const authAssignmentApi = {
  /**
   * Lista todas as atribuições de acesso
   */
  async list(params: AuthAssignmentListParams = {}): Promise<AuthAssignmentListResponse> {
    const queryParams = new URLSearchParams();

    if (params.search) queryParams.append('search', params.search);
    if (params.profileName) queryParams.append('profile_name', params.profileName);
    if (params.profileType !== undefined) queryParams.append('profile_type', String(params.profileType));
    if (params.page) queryParams.append('page', String(params.page));
    if (params.pageSize) queryParams.append('page_size', String(params.pageSize));

    const url = `${ASSIGNMENTS_URL}?${queryParams.toString()}`;
    const response = await api.get<PythonAuthAssignmentListResponse>(url);

    return {
      items: response.items.map(transformAssignment),
      total: response.total,
      page: response.page,
      pageSize: response.page_size,
      pages: response.pages,
    };
  },

  /**
   * Cria uma nova atribuição de acesso
   */
  async create(data: AuthAssignmentCreate): Promise<AuthAssignmentItem> {
    const response = await api.post<PythonAuthAssignmentResponse>(ASSIGNMENTS_URL, data);
    return transformAssignment(response);
  },

  /**
   * Remove uma atribuição de acesso
   */
  async delete(itemname: string, userid: number): Promise<void> {
    await api.delete(`${ASSIGNMENTS_URL}/${encodeURIComponent(itemname)}/${userid}`);
  },
};

export const accessApi = {
  listAccessProfiles,
  getAccessProfile,
  createAccessProfile,
  updateAccessProfile,
  deleteAccessProfile,
  listProfileChildren,
  addProfileChild,
  removeProfileChild,
  listProfileAssignments,
  countProfileUsers,
  // Login logs
  listLoginLogs,
  listLoginLogActions,
  getLoginLogAggregation,
  exportLoginLogs,
  // Image Types
  personImageType: personImageTypeApi,
  // Relationship Types
  personRelationshipType: personRelationshipTypeApi,
  // Family Relationship Types
  familyRelationshipType: familyRelationshipTypeApi,
  // Person Logins
  personLogin: personLoginApi,
  // Auth Assignments
  authAssignment: authAssignmentApi,
};

export default accessApi;
